/**
 * Progress persistence, keyed by DAY rather than by level id.
 *
 * The schedule is the product now: day 200 is a thing the player can talk
 * about, and the level that fills it is an implementation detail the curator
 * may swap on the next re-tune. Storing day numbers means re-running the
 * curator does not wipe anyone's progress.
 *
 * Separate keys from the audio module's own mute flag (see
 * audio/engine.ts MUTE_STORAGE_KEY) — that one is owned by audio and read
 * before this module exists in the load order.
 */

const COMPLETED_KEY = "pathpuzzle.completedDays";
const CURRENT_DAY_KEY = "pathpuzzle.currentDay";

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* not persisting is survivable */
  }
}

export function getCompletedDays(): Set<number> {
  const raw = readJSON<number[]>(COMPLETED_KEY, []);
  return new Set(Array.isArray(raw) ? raw.filter((n) => typeof n === "number") : []);
}

export function markDayCompleted(day: number): void {
  const done = getCompletedDays();
  if (done.has(day)) return;
  done.add(day);
  writeJSON(COMPLETED_KEY, [...done].sort((a, b) => a - b));
}

export function getCurrentDay(): number | null {
  const raw = readJSON<number | null>(CURRENT_DAY_KEY, null);
  return typeof raw === "number" ? raw : null;
}

export function setCurrentDay(day: number): void {
  writeJSON(CURRENT_DAY_KEY, day);
}
